<template>
    <div class="helix-container">

        <!-- Home Page -->
        <div v-if="homePage" class="home-wrapper">
            <component :is="dynamicComponent" :homeImage="homeImage" :exhibitIdProps="exhibitId" @exit="exitHomePage" />
        </div>

        <!-- Helix Gallery -->
        <div v-else class="gallery-scene">

            <!-- HUD -->
            <div class="hud">
                <h1 class="hud-title">{{ exhibitName }}</h1>
                <div class="scroll-indicator">
                    <span>Scrollez pour explorer</span>
                    <div class="line"></div>
                </div>
                <button class="menu-btn" @click="homePage = true">Menu</button>
            </div>

            <!-- 3D World -->
            <div class="viewport">
                <div class="helix-world" ref="worldRef">

                    <!-- Central Pillar (Visual Decor) -->
                    <div class="pillar"></div>

                    <!-- Items -->
                    <div v-for="(resource, index) in resources" :key="index" class="helix-item"
                        :style="getItemStyle(index)" @click="openLightbox(index)">
                        <div class="card-content">
                            <img :src="resourceUrls[index]" class="card-img" loading="lazy" />
                            <div class="card-info">
                                <h3>{{ resource.title }}</h3>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <!-- Scroll Spacer -->
            <div class="scroll-spacer" :style="{ height: spacerHeight + 'px' }"></div>

            <!-- Lightbox -->
            <transition name="fade">
                <div v-if="selectedImageIndex !== null" class="lightbox" @click="closeLightbox">
                    <div class="lightbox-content" @click.stop>
                        <button class="close-btn" @click="closeLightbox">&times;</button>
                        <img :src="resourceUrls[selectedImageIndex]" class="lightbox-full-img" />
                        <div class="lightbox-details">
                            <h2>{{ resources[selectedImageIndex].title }}</h2>
                            <p>{{ resources[selectedImageIndex].description }}</p>
                        </div>
                    </div>
                </div>
            </transition>

        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineProps } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import gsap from 'gsap';

// Models
import Modele_1 from "../../components/Models/Modele_1.vue";
import Modele_2 from "../../components/Models/Modele_2.vue";
import Modele_3 from "../../components/Models/Modele_3.vue";
import Modele_4 from "../../components/Models/Modele_4.vue";
import Modele_5 from "../../components/Models/Modele_5.vue";
import Modele_6 from "../../components/Models/Modele_6.vue";
import Modele_7 from "../../components/Models/Modele_7.vue";

const props = defineProps({
    homePageProps: { type: Boolean, default: true },
    exhibitId: { type: String, default: null }
});

const route = useRoute();
const id = ref(props.exhibitId ?? route.params.id);
const homePage = ref(props.homePageProps);
const selectedTemplate = ref("");
const exhibitName = ref("");
const resources = ref([]);
const resourceUrls = ref([]);
const homeImage = ref({});

// Models Mapping
const modeleComponents = { Modele_1, Modele_2, Modele_3, Modele_4, Modele_5, Modele_6, Modele_7 };
const dynamicComponent = computed(() => modeleComponents[selectedTemplate.value] || null);

// Helix Logic
const worldRef = ref(null);
const spacerHeight = ref(5000);
const ITEM_HEIGHT = 400; // Vertical distance per item
const RADIUS = 600;      // Distance from center
const ROTATION_PER_ITEM = 60; // Degrees

// Lightbox
const selectedImageIndex = ref(null);

const getItemStyle = (index) => {
    const angle = index * ROTATION_PER_ITEM;
    const y = index * ITEM_HEIGHT;

    return {
        transform: `rotateY(${angle}deg) translateZ(${RADIUS}px) translateY(${y}px)`
    };
};

const handleScroll = () => {
    if (homePage.value) return;

    const scrollTop = window.scrollY;

    // We move the world DOWN (negative Y) to simulate moving UP
    // We also rotate the world in reverse to keep items facing camera or creating dynamic spin
    const targetY = -scrollTop;
    const targetRotation = -scrollTop * (ROTATION_PER_ITEM / ITEM_HEIGHT);

    gsap.to(worldRef.value, {
        y: targetY + window.innerHeight / 2, // Center the view
        rotationY: targetRotation,
        duration: 1,
        ease: "power2.out"
    });
};

const exitHomePage = () => {
    homePage.value = false;
    setTimeout(() => window.scrollTo(0, 0), 100);
};

const fetchData = async () => {
    try {
        const baseUrl = process.env.VUE_APP_BASEURL;
        const exhibitParam = id.value;

        const [exhibitRes, modelRes, homeRes] = await Promise.all([
            axios.get(`${baseUrl}/exhibit/${exhibitParam}`),
            axios.get(`${baseUrl}/model/${exhibitParam}`),
            axios.get(`${baseUrl}/exhibit_home/${exhibitParam}`),
        ]);

        exhibitName.value = exhibitRes.data.exhibit_name;
        homeImage.value = homeRes.data[0];
        if (modelRes.data.length > 0) {
            selectedTemplate.value = modelRes.data[0].model.replace(/è/g, 'e').replace(/\s+/g, '_');
        }

        const resourcesRes = await axios.post(`${baseUrl}/resources`, { exhibitId: exhibitParam });
        const galleryRes = await axios.get(`${baseUrl}/galleryResources/${exhibitParam}`);
        const galleryIds = galleryRes.data.map(r => r.resource_id);
        const filtered = resourcesRes.data.filter(r => galleryIds.includes(r.id));
        resources.value = filtered;

        const urls = await Promise.all(filtered.map(r => axios.get(`${baseUrl}/s3Resource/${r.name}`)));
        resourceUrls.value = urls.map(r => r.data);

        // Set scroll height
        spacerHeight.value = (filtered.length * ITEM_HEIGHT) + window.innerHeight;

    } catch {
        resources.value = [];
        resourceUrls.value = [];
    }
};

const openLightbox = (i) => selectedImageIndex.value = i;
const closeLightbox = () => selectedImageIndex.value = null;

onMounted(() => {
    fetchData();
    window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@300;500&display=swap');

.helix-container {
    background: #000;
    min-height: 100vh;
    color: #fff;
    font-family: 'Oswald', sans-serif;
    overflow-x: hidden;
}

.gallery-scene {
    position: relative;
    /* Fixed viewport effectively */
}

/* HUD */
.hud {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    padding: 2rem;
    z-index: 10;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    pointer-events: none;
}

.hud-title {
    margin: 0;
    font-size: 2.5rem;
    text-transform: uppercase;
    letter-spacing: 4px;
}

.menu-btn {
    pointer-events: auto;
    background: transparent;
    border: 1px solid #fff;
    color: #fff;
    padding: 10px 20px;
    cursor: pointer;
    transition: all 0.3s;
}

.menu-btn:hover {
    background: #fff;
    color: #000;
}

.scroll-indicator {
    position: fixed;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    opacity: 0.7;
}

.scroll-indicator .line {
    width: 1px;
    height: 60px;
    background: #fff;
    margin: 10px auto 0;
    animation: grow 2s infinite;
}

/* 3D Viewport */
.viewport {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    perspective: 1500px;
    overflow: hidden;
    background: radial-gradient(circle at center, #1a1a1a 0%, #000 80%);
}

.helix-world {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    /* World origins from center */
}

.pillar {
    position: absolute;
    left: 50%;
    top: -5000px;
    /* Tall pillar */
    width: 2px;
    height: 10000px;
    background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.2), transparent);
    transform: translateX(-50%);
}

.helix-item {
    position: absolute;
    top: 0;
    left: 50%;
    width: 340px;
    height: 240px;
    margin-left: -170px;
    margin-top: -120px;
    transform-style: preserve-3d;
    cursor: pointer;
}

.card-content {
    width: 100%;
    height: 100%;
    background: rgba(20, 20, 20, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.2);
    display: flex;
    flex-direction: column;
    transition: transform 0.3s, background 0.3s;
}

.helix-item:hover .card-content {
    transform: scale(1.1);
    background: rgba(40, 40, 40, 0.9);
    border-color: #fff;
}

.card-img {
    width: 100%;
    height: 80%;
    object-fit: cover;
}

.card-info {
    height: 20%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.card-info h3 {
    margin: 0;
    font-size: 0.9rem;
    letter-spacing: 1px;
}

/* Spacer */
.scroll-spacer {
    width: 1px;
}

/* Lightbox */
.lightbox {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.95);
    z-index: 100;
    display: flex;
    justify-content: center;
    align-items: center;
}

.lightbox-content {
    max-width: 90%;
    max-height: 90%;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
}

.lightbox-full-img {
    max-height: 70vh;
    box-shadow: 0 0 30px rgba(255, 255, 255, 0.1);
}

.lightbox-details {
    margin-top: 1rem;
    text-align: center;
    max-width: 600px;
}

.close-btn {
    position: absolute;
    top: -40px;
    right: 0;
    background: none;
    border: none;
    font-size: 2rem;
    color: #fff;
    cursor: pointer;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@keyframes grow {
    0% {
        height: 10px;
        opacity: 0;
    }

    50% {
        height: 60px;
        opacity: 1;
    }

    100% {
        height: 60px;
        opacity: 0;
        transform: translateY(20px);
    }
}
</style>
